import { FC } from 'react';
import { Timeline, Typography, theme } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';

const { Text } = Typography;

interface TrackingHistoryTimelineProps {
    trackingHistory?: Array<{
        timestamp: Timestamp;
        status: string;
        location?: string;
        details?: string;
    }>;
}

const TrackingHistoryTimeline: FC<TrackingHistoryTimelineProps> = ({ trackingHistory }) => {
    const { token } = theme.useToken();

    if (!trackingHistory || trackingHistory.length === 0) {
        return (
            <FlexCol style={{ width: '100%', padding: 20 }}>
                <Text type="secondary">No tracking history available</Text>
            </FlexCol>
        );
    }

    const getStatusIcon = (status: string) => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes('delivered')) {
            return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
        }
        if (lowerStatus.includes('out for delivery')) {
            return <ClockCircleOutlined style={{ color: token.colorPrimary }} />;
        }
        return <InfoCircleOutlined style={{ color: token.colorTextSecondary }} />;
    };

    const getStatusColor = (status: string) => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes('delivered')) return token.colorSuccess;
        if (lowerStatus.includes('out for delivery')) return token.colorPrimary;
        return token.colorTextSecondary;
    };

    const sortedHistory = [...trackingHistory].sort((a, b) => {
        const timeA = a.timestamp instanceof Timestamp ? a.timestamp.toMillis() : 0;
        const timeB = b.timestamp instanceof Timestamp ? b.timestamp.toMillis() : 0;
        return timeB - timeA;
    });

    const timelineItems = sortedHistory.map((event, index) => {
        const timestamp = event.timestamp instanceof Timestamp
            ? dayjs(event.timestamp.toDate()).format('MMM D, YYYY h:mm A')
            : 'Invalid Date';

        return {
            dot: getStatusIcon(event.status),
            color: getStatusColor(event.status),
            children: (
                <FlexCol noGap style={{ marginBottom: index < sortedHistory.length - 1 ? 16 : 0 }}>
                    <Text strong>{event.status}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {timestamp}
                    </Text>
                    {event.location && (
                        <FlexBox gap={4}>
                            <EnvironmentOutlined style={{ fontSize: 12, color: token.colorTextSecondary }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {event.location}
                            </Text>
                        </FlexBox>
                    )}
                    {event.details && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {event.details}
                        </Text>
                    )}
                </FlexCol>
            )
        };
    });

    return (
        <Timeline
            items={timelineItems}
            style={{ marginTop: 10 }}
        />
    );
};

export default TrackingHistoryTimeline;


